import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, Lock, User, Loader2, Languages, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";

const formatError = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

export const AuthPage = () => {
  const { t, lang, theme, toggleLang, toggleTheme } = useApp();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (isRegister) {
        await register(name.trim(), email.trim(), password);
        toast.success(t("registerSuccess"));
      } else {
        await login(email.trim(), password);
        toast.success(t("loginSuccess"));
      }
    } catch (err) {
      toast.error(formatError(err, t("authError")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top bar: language + theme */}
      <div className="flex justify-end gap-1 p-4">
        <button
          data-testid="auth-lang-toggle"
          onClick={toggleLang}
          className="flex items-center gap-1.5 h-10 px-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        >
          <Languages className="w-4 h-4" />
          <span className="uppercase">{lang}</span>
        </button>
        <button
          data-testid="auth-theme-toggle"
          onClick={toggleTheme}
          className="w-10 h-10 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        >
          {theme === "light" ? <Moon className="w-[18px] h-[18px]" /> : <Sun className="w-[18px] h-[18px]" />}
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="w-full max-w-sm"
          data-testid="auth-card"
        >
          <div className="flex flex-col items-center mb-7">
            <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center shadow-lg mb-4">
              <CheckCircle2 className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="font-heading font-bold text-2xl tracking-tight">
              {isRegister ? t("authRegisterTitle") : t("authLoginTitle")}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 text-center">
              {isRegister ? t("authRegisterSub") : t("authLoginSub")}
            </p>
          </div>

          <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] space-y-4">
            {isRegister && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">{t("name")}</label>
                <div className="relative mt-1.5">
                  <User className="w-[18px] h-[18px] text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("namePlaceholder")}
                    className="w-full h-12 bg-background border border-border rounded-xl pl-11 pr-4 text-base outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">{t("email")}</label>
              <div className="relative mt-1.5">
                <Mail className="w-[18px] h-[18px] text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("emailPlaceholder")}
                  autoComplete="email"
                  className="w-full h-12 bg-background border border-border rounded-xl pl-11 pr-4 text-base outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">{t("password")}</label>
              <div className="relative mt-1.5">
                <Lock className="w-[18px] h-[18px] text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="auth-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("passwordPlaceholder")}
                  autoComplete={isRegister ? "new-password" : "current-password"}
                  className="w-full h-12 bg-background border border-border rounded-xl pl-11 pr-4 text-base outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all shadow-sm disabled:opacity-60"
            >
              {busy && <Loader2 className="w-5 h-5 animate-spin" />}
              {isRegister ? t("register") : t("login")}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-5">
            {isRegister ? t("haveAccount") : t("noAccount")}{" "}
            <button
              data-testid="auth-switch-mode"
              onClick={() => setMode(isRegister ? "login" : "register")}
              className="font-semibold text-primary hover:underline"
            >
              {isRegister ? t("login") : t("register")}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
};
